import React from 'react';
import { Link } from 'react-router-dom';
import { MailIcon, MapPinIcon, CalendarIcon, CheckIcon, LinkedinIcon } from 'lucide-react';
import { Container, Section, Kicker } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { Field, inputClass, ConsentCheckbox } from '../components/ui/Field';
import { MeetingScheduler } from '../components/MeetingScheduler';
import { SERVICE_LINES } from '../data/services';
import { submitLead } from '../lib/leads';
import { track } from '../lib/analytics';
import { LINKEDIN_URL, CONTACT_EMAIL } from '../lib/seo';
const ENQUIRY_TYPES = [{
  id: 'intelligence',
  label: 'A market or sector question'
}, {
  id: 'growth',
  label: 'Growth, pricing or pipeline'
}, {
  id: 'ai',
  label: 'AI adoption or readiness'
}, {
  id: 'other',
  label: 'Something else'
}];
export function Contact() {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [organisation, setOrganisation] = React.useState('');
  const [enquiry, setEnquiry] = React.useState('intelligence');
  const [message, setMessage] = React.useState('');
  const [consent, setConsent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const routedLine = SERVICE_LINES.find(line => line.id === enquiry);
  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const result = await submitLead({
      email,
      firstName: name,
      organisation,
      consent,
      source: 'contact-form',
      line: enquiry === 'other' ? undefined : enquiry,
      notes: message
    });
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error ?? 'Something went wrong. Please try again.');
      return;
    }
    setSent(true);
  };
  return <>
      <section className="surface-dark" data-id="element-841" data-mp="2bxlq7kvxrzaj">
        <Container className="py-14 sm:py-20" data-id="element-842" data-mp="1m6u2qmmzw1e0">
          <Kicker data-id="element-843" data-mp="1m5iry2v4ttde">Contact</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-844" data-mp="1h8iv8ik6a2yy">
            Tell us the decision in front of you
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-845" data-mp="1w3gnzcj8gugk">
            A discovery call lasts thirty minutes. We will tell you honestly whether we are the right firm for the
            problem, and what we would do first if we are.
          </p>
        </Container>
      </section>

      <Section data-id="element-846" data-mp="yjlz0957t0e3">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr]" data-id="element-847" data-mp="yfy4p7ry49yb">
          <div data-id="element-848" data-mp="1d7grbofb92qn">
            <h2 className="font-display text-[26px] text-ink" data-id="element-849" data-mp="1d801uk2msoun">Send us a message</h2>
            {sent ? <div className="mt-6 rounded-md border border-line bg-mist p-7" data-id="element-850" data-mp="18dvtbwdpw8rg">
                <div className="flex items-center gap-2 text-gold" data-id="element-851" data-mp="kote0v6k2s04">
                  <CheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-852" data-mp="fs2xk6qu9ue6" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" data-id="element-853" data-mp="2remv5syctknq">Received</span>
                </div>
                <h3 className="mt-4 font-display text-[22px] text-ink" data-id="element-854" data-mp="2w5msv0fas6ra">Thank you, we have your enquiry</h3>
                <p className="mt-3 text-[15px] text-body" data-id="element-855" data-mp="2hhia5wg8vv8i">
                  {routedLine ? `We have routed this to the ${routedLine.name} team. ` : 'We have routed this to the right team. '}
                  You will hear from us within one working day. If it is urgent, book a call using the scheduler.
                </p>
              </div> : <form onSubmit={onSubmit} className="mt-6 space-y-5" data-id="element-856" data-mp="313en413f04yn">
                <div className="grid gap-5 sm:grid-cols-2" data-id="element-857" data-mp="355rs2nac7yud">
                  <Field id="c-name" label="Your name" required data-id="element-858" data-mp="3548bswoolnsl">
                    <input id="c-name" type="text" required value={name} onChange={event => setName(event.target.value)} className={inputClass} data-id="element-859" data-mp="1po81gkrokacm" />
                  </Field>
                  <Field id="c-email" label="Work email" required error={error ?? undefined} data-id="element-860" data-mp="255znmvofs74x">
                    <input id="c-email" type="email" required value={email} onChange={event => setEmail(event.target.value)} placeholder="you@organisation.org" className={inputClass} data-id="element-861" data-mp="208r16iw99gxv" />
                  </Field>
                </div>
                <Field id="c-org" label="Organisation" required data-id="element-862" data-mp="2iww3dlx9ktuk">
                  <input id="c-org" type="text" required value={organisation} onChange={event => setOrganisation(event.target.value)} className={inputClass} data-id="element-863" data-mp="2ew2eap835903" />
                </Field>
                <Field id="c-enquiry" label="What is your enquiry about" required hint="We use this to route your message to the right service line." data-id="element-864" data-mp="3f6veanfipo16">
                  <select id="c-enquiry" value={enquiry} onChange={event => setEnquiry(event.target.value)} className={inputClass} data-id="element-865" data-mp="3swp1z2fae29b">
                    {ENQUIRY_TYPES.map(type => <option key={type.id} value={type.id} data-id="element-866" data-mp="6rboxzhywpre">
                        {type.label}
                      </option>)}
                  </select>
                </Field>
                <Field id="c-message" label="Your message" required data-id="element-867" data-mp="fnszb0iicjm3">
                  <textarea id="c-message" required rows={5} value={message} onChange={event => setMessage(event.target.value)} placeholder="The decision you are facing, and any timing we should know about." className={inputClass} data-id="element-868" data-mp="2ck085xxczwgc" />
                </Field>
                <ConsentCheckbox id="c-consent" checked={consent} onChange={setConsent} data-id="element-869" data-mp="3tr18zd33pii8">
                  I am happy for Cavalla Solution to contact me about this enquiry. See our{' '}
                  <Link to="/legal/privacy" className="underline underline-offset-2" data-id="element-870" data-mp="7k31slndqp2z">
                    privacy notice
                  </Link>
                  .
                </ConsentCheckbox>
                <Button type="submit" size="lg" disabled={submitting} data-id="element-871" data-mp="gdg2wavlkx05">
                  {submitting ? 'Sending' : 'Send enquiry'}
                </Button>
              </form>}
          </div>

          <div className="space-y-8" data-id="element-872" data-mp="qcoskrjfjpgp">
            <div className="rounded-md border border-line bg-mist p-6 sm:p-7" data-id="element-873" data-mp="ldklc6g2g1t0">
              <div className="flex items-center gap-2 text-gold" data-id="element-874" data-mp="lbhnx1cl36e9">
                <CalendarIcon className="h-4 w-4" aria-hidden="true" data-id="element-875" data-mp="14znhaeqwk5rq" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.2em]" data-id="element-876" data-mp="19ygu04yd7squ">Scheduler</span>
              </div>
              <h2 className="mt-4 font-display text-[22px] text-ink" data-id="element-877" data-mp="v0erzz6zffbp">Book a discovery call</h2>
              <p className="mt-3 text-[15px] text-body" data-id="element-878" data-mp="zypzi39t1dqr">
                Thirty minutes with our founder. Choose a slot that suits your time zone.
              </p>
              <MeetingScheduler line={enquiry} className="mt-6" data-id="element-879" data-mp="qzuvmxu8gfqm" />
            </div>

            <div className="rounded-md border border-line p-6 sm:p-7" data-id="element-880" data-mp="caghlcfppa4w">
              <h2 className="font-display text-[20px] text-ink" data-id="element-881" data-mp="c8c72bnb5cvn">Reach us directly</h2>
              <ul className="mt-5 space-y-4 text-[15px] text-body" data-id="element-882" data-mp="ha1n65pe0gql">
                <li className="flex gap-3" data-id="element-883" data-mp="39ihh58e9mrl">
                  <MailIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-884" data-mp="3bv4hwt0enbi" />
                  <a href={`mailto:${CONTACT_EMAIL}`} className="underline-offset-4 hover:underline" data-id="element-885" data-mp="7akj1spsaawq">
                    {CONTACT_EMAIL}
                  </a>
                </li>
                <li className="flex gap-3" data-id="element-886" data-mp="it342dp677jg">
                  <LinkedinIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-887" data-mp="dsovmujnpvkw" />
                  <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="underline-offset-4 hover:underline" data-id="element-888" data-mp="8tm4me0ihpvx">
                    Follow us on LinkedIn
                    <span className="sr-only" data-id="element-889" data-mp="3w5dc7wpwtfic">, opens in a new tab</span>
                  </a>
                </li>
                <li className="flex gap-3" data-id="element-890" data-mp="3r58slxtpg4nw">
                  <MapPinIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-891" data-mp="3m9a1w3kwmakb" />
                  <span data-id="element-892" data-mp="3h9nn3ppfgm9e">
                    Working across West and East Africa, with data held in the European Union.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Section>
    </>;
}